import { useEffect, useRef, useState } from "react";

export const formatMMSS = (ms) => {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

/**
 * Server-synced countdown timer.
 *
 * Reads `server_time` from the polled payload to estimate the client↔server clock skew,
 * then converts `timer_end_time` to a remaining duration that stays in sync across every
 * screen (Facilitator Dashboard, Participant, Station, Host, Presentation) even if the
 * device clocks differ.
 *
 * @param {object} payload
 * @param {boolean} payload.timer_active
 * @param {string|null} payload.timer_end_time  ISO timestamp when the timer ends
 * @param {string|null} payload.server_time     ISO timestamp of "now" on the server when payload was generated
 *
 * @returns { remaining_ms, seconds, mmss, active, expired, offset_ms }
 */
export function useServerSyncedTimer(payload) {
  const { timer_active, timer_end_time, server_time } = payload || {};
  const offsetRef = useRef(0); // client_now_ms - server_now_ms
  const [, force] = useState(0);

  // Re-estimate offset whenever a new server_time arrives.
  useEffect(() => {
    if (!server_time) return;
    const serverNow = new Date(server_time).getTime();
    if (Number.isFinite(serverNow)) {
      offsetRef.current = Date.now() - serverNow;
    }
  }, [server_time]);

  // Tick 4x/second while timer is active so the rendered countdown stays smooth.
  useEffect(() => {
    if (!timer_active || !timer_end_time) return;
    const id = setInterval(() => force((x) => (x + 1) & 0xffff), 250);
    return () => clearInterval(id);
  }, [timer_active, timer_end_time]);

  const end = timer_end_time ? new Date(timer_end_time).getTime() : 0;
  const correctedNow = Date.now() - offsetRef.current; // approximate server "now"
  const remaining = end > 0 ? Math.max(0, end - correctedNow) : 0;

  return {
    remaining_ms: remaining,
    seconds: Math.ceil(remaining / 1000),
    mmss: formatMMSS(remaining),
    active: !!timer_active,
    expired: !!(timer_active && timer_end_time && remaining <= 0),
    offset_ms: offsetRef.current,
  };
}

export default useServerSyncedTimer;
